/**
 * =============================================================
 * apiCall.js (ENTERPRISE / MNC STANDARD – PRODUCTION SAFE)
 * -------------------------------------------------------------
 * Responsibilities:
 *  - Axios request execution
 *  - Retry with exponential backoff
 *  - AbortController support
 *  - Response normalization
 *  - Safe cURL logging (dev-only)
 *  - Performance timing
 *
 * SAFE ENHANCEMENT:
 *  ✅ Supports query params (pagination / search)
 *  ✅ Backward compatible (will NOT break existing APIs)
 * =============================================================
 */

import axiosClient from "../axiosClient";
import logger from "../../utils/logger";

/* =============================================================
   ENV CONFIG
============================================================= */
const IS_PROD = process.env.NODE_ENV === "production";
const ENABLE_CURL_LOGS = !IS_PROD;
const ENABLE_TIMING_LOGS = !IS_PROD;

const MAX_RETRIES = 2;
const BASE_RETRY_DELAY_MS = 300;

/* =============================================================
   SAFE JSON STRINGIFY
============================================================= */
const safeStringify = (data) => {
  try {
    return JSON.stringify(data, null, 2);
  } catch {
    return '"[Unserializable payload]"';
  }
};

/* =============================================================
   cURL LOGGER (DEV ONLY – WITH QUERY PARAM SUPPORT)
============================================================= */
const printCurl = (method, url, payload = null, params = {}) => {
  if (!ENABLE_CURL_LOGS) return;

  const baseURL = axiosClient.defaults.baseURL || "";

  // ✅ Safely build query string
  const queryString =
    params && Object.keys(params).length > 0
      ? "?" + new URLSearchParams(params).toString()
      : "";

  const headers = [
    `-H "Authorization: Bearer <AUTO-INJECTED>"`,
    `-H "Content-Type: application/json"`,
  ].join(" \\\n  ");

  const dataPart = payload
    ? ` \\\n  -d '${safeStringify(payload)}'`
    : "";

  console.log(`
==============================
🚀 cURL REQUEST
curl -X ${method.toUpperCase()} "${baseURL}${url}${queryString}" \\
  ${headers}${dataPart}
==============================`);
};

/* =============================================================
   PERFORMANCE TIMER
============================================================= */
const now = () => performance?.now?.() ?? Date.now();

/* =============================================================
   RETRY HELPERS
============================================================= */
const delay = (ms) => new Promise((r) => setTimeout(r, ms));

const shouldRetry = (error) =>
  error?.code === "ECONNABORTED" ||
  error?.message?.includes("Network Error");

/* =============================================================
   MAIN API CALL FUNCTION
============================================================= */
export const apiCall = async (
  method,
  url,
  payload = null,
  endpointName = "API Call",
  options = {}
) => {

  /**
   * =============================================================
   * SAFE CONFIG OBJECT
   * -------------------------------------------------------------
   * IMPORTANT:
   *  - params support added for pagination/search
   *  - backward compatible
   * =============================================================
   */
  // ✅ Grant a longer timeout (120 seconds) for FormData file uploads
  const isFormData = payload instanceof FormData;
  const timeoutValue = isFormData ? 120000 : (options.timeout || 30000);

  const config = {
    signal: options.signal,
    params: options.params || {}, // ✅ CRITICAL FIX (pagination support)
    responseType: options.responseType || "json", // ✅ SAFE ADDITION
    timeout: timeoutValue, // ✅ Applied safe timeout for large binaries
  };

  // Print cURL (dev only)
  printCurl(method, url, payload, config.params);

  let attempt = 0;
  const startTime = now();

  while (attempt <= MAX_RETRIES) {
    try {
      let response;

      switch (method.toUpperCase()) {
        case "GET":
          response = await axiosClient.get(url, config);
          break;

        case "POST":
          response = await axiosClient.post(url, payload, config);
          break;

        case "PUT":
          response = await axiosClient.put(url, payload, config);
          break;

        case "DELETE":
          response = await axiosClient.delete(url, config);
          break;

        default:
          throw new Error(`Unsupported HTTP method: ${method}`);
      }

      /**
       * =============================================================
       * SUCCESS NORMALIZATION
       * =============================================================
       */

// ✅ Handle file downloads (blob) separately
if (config.responseType === "blob") {

  if (response.status >= 200 && response.status < 300) {

    if (ENABLE_TIMING_LOGS) {
      console.log(
        `⏱️ ${endpointName} completed in ${(now() - startTime).toFixed(1)}ms`
      );
    }

    return response.data; // return raw blob safely
  }

  throw new Error("File download failed");
}

      const isSuccess =
        response.status >= 200 &&
        response.status < 300 &&
        (
          response.data?.success === true ||
          response.data?.status === true ||
          response.data?.status === "success" ||
          response.data?.success === undefined
        );

      if (!isSuccess) {
        throw new Error(response.data?.message || "Request failed");
      }

      if (ENABLE_TIMING_LOGS) {
        console.log(
          `⏱️ ${endpointName} completed in ${(now() - startTime).toFixed(1)}ms`
        );
      }

      return response.data;

    } catch (error) {

      /* ================= Abort ================= */
      if (
        error?.name === "CanceledError" ||
        error?.code === "ERR_CANCELED"
      ) {
        return { success: false, message: "Request cancelled" };
      }

      /* ================= Retry ================= */
      if (attempt < MAX_RETRIES && shouldRetry(error)) {

        const backoffDelay =
          BASE_RETRY_DELAY_MS * Math.pow(2, attempt);

        if (!IS_PROD) {
          console.warn(
            `🔁 Retry ${attempt + 1}/${MAX_RETRIES} for ${endpointName} after ${backoffDelay}ms`
          );
        }

        attempt++;
        await delay(backoffDelay);
        continue;
      }

      /* ================= Final Failure ================= */
      const message =
        error?.response?.data?.message ||
        error?.message ||
        `Unable to ${endpointName.toLowerCase()}`;

      logger.error(endpointName, {
        url,
        method,
        attempt,
        error: error?.response?.data || error,
      });

      return { success: false, message };
    }
  }
};

export default { apiCall };
